import React from "react";
import { useState } from "react";
import { Input } from "components/Graphs/Input";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import {
  GitBranch,
  Upload,
  MonitorSmartphone,
  Box,
  Server,
  ContainerIcon,
} from "lucide-react";
import { FlatCard } from "components/UI-lib";
import { motion } from "framer-motion";
import { FadeInDiv } from "components/Motion/FadeInDiv";
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  CodeBracketIcon,
  CommandLineIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { RadioCardItem } from "components/Graphs/RadioCardGroup";
import { RadioCardGroup } from "components/Graphs/RadioCardGroup";
import { RuntimeType } from "./launchable-creator";
import GithubIcon from "components/Icons/GithubIcon";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
interface CodePathFormProps {
  codeSource: string;
  onCodeSourceChange: (source: string) => void;
  runtimeType: RuntimeType;
  onRuntimeSelect: (runtime: RuntimeType) => void;
  codeSourceType: "url/upload" | "embedded" | "none" | "";
  setCodeSourceType: (type: "url/upload" | "embedded" | "none") => void;
  codeSourceError: string | null;
  setCodeSourceError: (error: string | null) => void;
}

export const CodePathForm: React.FC<CodePathFormProps> = ({
  codeSource,
  onCodeSourceChange,
  runtimeType,
  onRuntimeSelect,
  codeSourceType,
  setCodeSourceType,
  codeSourceError,
  setCodeSourceError,
}) => {
  const [activeTab, setActiveTab] = useState<string>("git");

  const checkCodeSourceError = (value: string) => {
    if (value.endsWith("/")) {
      setCodeSourceError("Invalid URL: Please remove the trailing slash.");
    } else if (
      value.match(
        /^(https:\/\/github\.com\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/tree\/[A-Za-z0-9_.-]+(?:\/[\S]*)?$|https:\/\/gitlab\.com\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+\/-\/tree\/[A-Za-z0-9_.-]+(?:\/[\S]*)?$)/
      )
    ) {
      setCodeSourceError(
        "Invalid URL: The provided link points to a directory, not a repository or file. Please provide a GitHub or GitLab repository or a file URL in raw or blob format"
      );
    } else {
      setCodeSourceError(null);
    }
  };

  return (
    <div>
      <div className="flex items-center mb-4">
        <CodeBracketIcon className="h-5 w-5 text-primary mr-2" />
        <h2 className="text-primary text-xl font-semibold">
          How would you like to provide your code files?
        </h2>
      </div>

      <RadioCardGroup
        key={"code-source"}
        value={codeSourceType}
        onValueChange={(value) =>
          setCodeSourceType(value as "url/upload" | "embedded" | "none")
        }
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <RadioCardItem
          value="url/upload"
          className="cursor-pointer hover:border-highlight transition-colors"
        >
          <div className="flex flex-row items-start justify-center space-y-1">
            <GitHubLogoIcon className="h-5 w-5 text-primary mr-1" />
            <p className="text-sm font-medium leading-none text-primary">
              I have code files in a git repository
            </p>
          </div>
        </RadioCardItem>

        <RadioCardItem
          value="embedded"
          className="cursor-pointer hover:border-highlight transition-colors"
        >
          <div className="flex flex-row items-start justify-center space-y-1">
            <ArchiveBoxArrowDownIcon className="h-5 w-5 text-primary mr-1" />
            <p className="text-sm font-medium leading-none text-primary">
              My code files are embedded in my container
            </p>
          </div>
        </RadioCardItem>
        <RadioCardItem
          value="none"
          className="cursor-pointer hover:border-highlight transition-colors"
        >
          <div className="flex flex-row items-start justify-center space-y-1">
            <CommandLineIcon className="h-5 w-5 text-primary mr-1" />
            <p className="text-sm font-medium leading-none text-primary">
              I don't have any code files
            </p>
          </div>
        </RadioCardItem>
      </RadioCardGroup>

      <div className="mt-6">
        {codeSourceType === "url/upload" && (
          <FadeInDiv className="space-y-4">
            <div>
              <h3 className="text-primary text-sm mb-1">
                Enter a public repository or notebook file URL
              </h3>
              <Input
                id="repo-url"
                placeholder="https://"
                value={activeTab === "git" ? codeSource : ""}
                onChange={(e) => {
                  onCodeSourceChange(e.target.value.trim());
                  checkCodeSourceError(e.target.value.trim());
                }}
              />
              {codeSourceError && (
                <p className="text-xs text-red-500 mt-1">{codeSourceError}</p>
              )}
              <p className="text-xs text-secondary mt-1">
                Examples: https://github.com/username/repo.git or
                https://github.com/username/repo/blob/main/notebook.ipynb
              </p>
            </div>
          </FadeInDiv>
        )}

        {codeSourceType === "embedded" && (
          <FadeInDiv className="flex flex-col items-center justify-center w-full">
            <div className="p-3 rounded-md text-sm text-secondary bg-secondaryBg flex flex-col w-full">
              <div className="flex flex-row text-amber-500">
                <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
                <h2 className="text-sm">Warning: Embedded Code Files</h2>
              </div>
              <p className="text-sm text-secondary mt-2">
                Code file artifacts within a container{" "}
                <strong>will not show up</strong> in the deployment preview of
                your launchable and is <strong>not recommended</strong>.
              </p>
              <p className="text-sm text-secondary">
                For the best user experience, we suggest adding your code files
                to a <strong>git repository</strong> and using the git
                repository option above.
              </p>
            </div>
          </FadeInDiv>
        )}
      </div>

      <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />

      {(codeSourceType === "none" || codeSourceType === "url/upload") && (
        <FadeInDiv>
          <div className="flex flex-col mb-4">
            <div className="flex items-center">
              <ComputerDesktopIcon className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-primary text-xl font-semibold">
                What type of runtime environment do you need?
              </h2>
            </div>
            <div className="flex flex-col mt-1">
              <p className="text-xs text-secondary">
                Note: For launchables requiring private container registries or
                api keys, <strong>use VM Mode</strong> and document image
                authentication steps in your uploaded notebook file for the best
                user experience.
              </p>
            </div>
          </div>
          <RadioCardGroup
            key={"runtime-type"}
            value={runtimeType}
            onValueChange={(value) => onRuntimeSelect(value as RuntimeType)}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <RadioCardItem value="VM">
              <div className="flex flex-col items-center space-y-1">
                <Server className="h-5 w-5 text-highlight mb-2" />
                <div className="space-y-1 text-center">
                  <p className="text-sm font-medium leading-none text-primary">
                    VM Mode
                  </p>
                  <p className="text-sm text-secondary">
                    Basic VM with Python installed
                  </p>
                </div>
              </div>
            </RadioCardItem>

            <RadioCardItem value="container">
              <div className="flex flex-col items-center">
                <Box className="mb-2 h-6 w-6 text-highlight" />
                <div className="flex items-center justify-center">
                  <p className="text-sm font-medium leading-none text-primary">
                    With container(s)
                  </p>
                </div>
                <p className="text-sm text-secondary">
                  Custom container environment
                </p>
              </div>
            </RadioCardItem>
          </RadioCardGroup>
        </FadeInDiv>
      )}
    </div>
  );
};
